// =============================================================================
// src/services/unleashedProductSync.ts
// -----------------------------------------------------------------------------
// PURE compare/plan for the Products sync (Function 3, first set). Given the slim
// products fetched from Unleashed (via the unleashed-fetch Edge Function) and our
// current unleashed_products rows, it produces a PLAN the results screen renders:
// what to create, update, mark obsolete, and how many are unchanged. It writes
// nothing — the UI applies the ticked subset via the unleashedProducts repo.
//
// Match key = product_code. Mirrors the preview-first Schools/Contacts importers.
// =============================================================================

import type { SlimProduct } from "../../supabase/functions/_shared/unleashed/productMap";
import type { AdminProductRow } from "../data/unleashedProducts";

// One field-level difference on a matched product (for the results screen).
export interface ProductChange {
  field: "description" | "group" | "base_pack" | "base_unit" | "status";
  from: string | null;
  to: string | null;
}

export interface ProductUpdate {
  id: number;
  code: string;
  next: SlimProduct;
  changes: ProductChange[];
}

export interface ProductObsolete {
  id: number;
  code: string;
  description: string | null;
}

export interface ProductSyncPlan {
  toCreate: SlimProduct[]; // codes not present locally (and not already obsolete)
  toUpdate: ProductUpdate[]; // present locally, one or more fields differ
  toObsolete: ProductObsolete[]; // present + active locally, now Obsolete in Unleashed
  unchangedCount: number;
  skippedNewObsolete: number; // new codes already Obsolete in Unleashed → nothing to do
}

// Compare the incoming Unleashed products against our current rows.
export function planProductSync(
  incoming: SlimProduct[],
  current: AdminProductRow[]
): ProductSyncPlan {
  const byCode = new Map<string, AdminProductRow>();
  for (const r of current) byCode.set(r.product_code, r);

  const plan: ProductSyncPlan = {
    toCreate: [],
    toUpdate: [],
    toObsolete: [],
    unchangedCount: 0,
    skippedNewObsolete: 0,
  };

  for (const inc of incoming) {
    if (!inc.product_code) continue;
    const cur = byCode.get(inc.product_code);

    if (!cur) {
      if (inc.obsolete) plan.skippedNewObsolete += 1;
      else plan.toCreate.push(inc);
      continue;
    }

    // A newly-obsolete product that's still active locally is a decision, not an
    // edit — surface it in its own bucket.
    if (inc.obsolete && !cur.deactivated) {
      plan.toObsolete.push({
        id: cur.id,
        code: cur.product_code,
        description: cur.product_description,
      });
      continue;
    }

    const changes: ProductChange[] = [];
    const cmp = (field: ProductChange["field"], from: string | null, to: string | null) => {
      if ((from ?? null) !== (to ?? null)) {
        changes.push({ field, from: from ?? null, to: to ?? null });
      }
    };
    cmp("description", cur.product_description, inc.product_description);
    cmp("group", cur.product_group, inc.product_group);
    cmp("base_pack", cur.base_pack, inc.base_pack);
    cmp("base_unit", cur.base_unit, inc.base_unit);
    // Reactivation: deactivated locally but active again in Unleashed.
    if (cur.deactivated && !inc.obsolete) {
      changes.push({ field: "status", from: "deactivated", to: "active" });
    }

    if (changes.length > 0) {
      plan.toUpdate.push({ id: cur.id, code: cur.product_code, next: inc, changes });
    } else {
      plan.unchangedCount += 1;
    }
  }

  return plan;
}
